import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { JourneyShell } from "@/components/journey/JourneyShell";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useJourneyContent } from "@/hooks/useJourneyContent";
import {
  completeFreeAssessment,
  completePaidAssessment,
  saveAssessmentAnswer,
  startAssessment,
} from "@/lib/journeyApi";
import "@/pages/Journey.css";

const shownQuestions = (allQuestions, tier, answers) =>
  allQuestions
    .filter((question) => question.tier === tier)
    .sort((a, b) => a.order - b.order)
    .filter((question) => {
      if (!question.show_if) return true;
      const [dependency, expected] = Object.entries(question.show_if)[0];
      return answers[dependency]?.answer === expected;
    });

export default function Assessment({ tier }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { ui, questions, loading } = useJourneyContent({ questions: true });
  const [assessment, setAssessment] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [followupMode, setFollowupMode] = useState(false);
  const [selectedValues, setSelectedValues] = useState([]);
  const [saving, setSaving] = useState(false);
  const startRequestId = useRef(window.crypto.randomUUID());

  useEffect(() => {
    let active = true;
    const forceNew = tier === "free" && searchParams.get("new") === "1";
    startAssessment(tier, forceNew, forceNew ? startRequestId.current : null)
      .then((data) => {
        if (!active) return;
        if (tier === "paid" && data.tier === "paid_pending") {
          navigate("/unlock", { replace: true });
          return;
        }
        if (tier === "paid" && data.tier === "paid") {
          navigate("/account", { replace: true });
          return;
        }
        setAssessment(data);
        if (forceNew) navigate("/check", { replace: true });
      })
      .catch(() => {
        if (active) navigate(tier === "paid" ? "/check" : "/account", { replace: true });
      });
    return () => {
      active = false;
    };
  }, [navigate, searchParams, tier]);

  const visible = useMemo(() => {
    if (!questions || !assessment) return [];
    return shownQuestions(questions.questions, tier, assessment.answers || {});
  }, [assessment, questions, tier]);
  const currentQuestion = visible[currentIndex];

  useEffect(() => {
    if (!assessment || visible.length === 0) return;
    const unanswered = visible.findIndex((question) => !assessment.answers?.[question.id]);
    setCurrentIndex(unanswered >= 0 ? unanswered : Math.max(0, visible.length - 1));
  }, [assessment, visible]);

  useEffect(() => {
    const answer = currentQuestion ? assessment?.answers?.[currentQuestion.id] : null;
    setFollowupMode(answer?.answer === "yes");
    setSelectedValues(answer?.followups || answer?.values || []);
  }, [assessment?.answers, currentQuestion]);

  if (loading || !ui || !questions || !assessment || !currentQuestion) {
    return <div className="journey-loading" data-testid="assessment-loading-state" />;
  }

  const sectionUi = tier === "free" ? ui.check : ui.screen;
  const progress = sectionUi.progress.replace("{n}", String(currentIndex + 1)).replace("{total}", String(visible.length));

  const finish = async (updated) => {
    if (tier === "free") {
      await completeFreeAssessment(updated.assessment_id);
      navigate("/report-ready", { replace: true });
    } else {
      await completePaidAssessment(updated.assessment_id);
      navigate("/unlock", { replace: true });
    }
  };

  const save = async (answer) => {
    if (saving) return;
    setSaving(true);
    try {
      const updated = await saveAssessmentAnswer(assessment.assessment_id, currentQuestion.id, answer);
      setAssessment(updated);
      const nextVisible = shownQuestions(questions.questions, tier, updated.answers || {});
      const nextIndex = nextVisible.findIndex((question, index) => index > currentIndex && !updated.answers?.[question.id]);
      if (nextIndex >= 0) {
        setCurrentIndex(nextIndex);
      } else if (nextVisible.every((question) => updated.answers?.[question.id])) {
        await finish(updated);
      }
    } finally {
      setSaving(false);
    }
  };

  const toggleValue = (value) => {
    const exclusive = new Set(currentQuestion.exclusive || []);
    setSelectedValues((current) => {
      if (exclusive.has(value)) return [value];
      const withoutExclusive = current.filter((item) => !exclusive.has(item));
      return withoutExclusive.includes(value)
        ? withoutExclusive.filter((item) => item !== value)
        : [...withoutExclusive, value];
    });
  };

  const toggleMultiRow = (event, value) => {
    if (event.target.closest('[role="checkbox"]')) return;
    toggleValue(value);
  };

  const optionButtons = (options, buildAnswer) => (
    <div className="journey-options" data-testid={`question-options-${currentQuestion.id}`}>
      {options.map((option) => (
        <button
          type="button"
          key={option.id}
          onClick={() => save(buildAnswer(option.id))}
          disabled={saving}
          data-testid={`question-${currentQuestion.id}-option-${option.id}`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );

  const multiOptions = currentQuestion.options || currentQuestion.followup?.options || [];
  const renderAnswer = () => {
    if (currentQuestion.type === "choice") {
      return optionButtons(currentQuestion.options, (value) => ({ value }));
    }
    if (currentQuestion.type === "multi") {
      return (
        <div className="journey-multi" data-testid={`question-multi-${currentQuestion.id}`}>
          {multiOptions.map((option) => (
            <label key={option.id} className={selectedValues.includes(option.id) ? "is-selected" : ""} onClick={(event) => toggleMultiRow(event, option.id)}>
              <Checkbox checked={selectedValues.includes(option.id)} onCheckedChange={() => toggleValue(option.id)} data-testid={`question-${currentQuestion.id}-checkbox-${option.id}`} />
              <span>{option.label}</span>
            </label>
          ))}
          <Button size="icon" onClick={() => save({ values: selectedValues })} disabled={!selectedValues.length || saving} aria-label="Continue" data-testid={`question-${currentQuestion.id}-continue-button`}>
            <ArrowRight aria-hidden="true" />
          </Button>
        </div>
      );
    }
    if (followupMode && currentQuestion.type === "yn_single") {
      return (
        <div className="journey-followup">
          <p data-testid={`question-${currentQuestion.id}-followup-prompt`}>{currentQuestion.followup.prompt}</p>
          {optionButtons(currentQuestion.followup.options, (followup) => ({ answer: "yes", followup }))}
        </div>
      );
    }
    if (followupMode && currentQuestion.type === "yn_multi") {
      return (
        <div className="journey-followup journey-multi">
          <p data-testid={`question-${currentQuestion.id}-followup-prompt`}>{currentQuestion.followup.prompt}</p>
          {multiOptions.map((option) => (
            <label key={option.id} className={selectedValues.includes(option.id) ? "is-selected" : ""} onClick={(event) => toggleMultiRow(event, option.id)}>
              <Checkbox checked={selectedValues.includes(option.id)} onCheckedChange={() => toggleValue(option.id)} data-testid={`question-${currentQuestion.id}-checkbox-${option.id}`} />
              <span>{option.label}</span>
            </label>
          ))}
          <Button size="icon" onClick={() => save({ answer: "yes", followups: selectedValues })} disabled={!selectedValues.length || saving} aria-label="Continue" data-testid={`question-${currentQuestion.id}-continue-button`}>
            <ArrowRight aria-hidden="true" />
          </Button>
        </div>
      );
    }
    return (
      <div className="journey-binary" data-testid={`question-binary-${currentQuestion.id}`}>
        <button type="button" onClick={() => currentQuestion.type === "yn" ? save({ answer: "yes" }) : setFollowupMode(true)} disabled={saving} data-testid={`question-${currentQuestion.id}-yes-button`}>{ui.check.yes}</button>
        <button type="button" onClick={() => save({ answer: "no" })} disabled={saving} data-testid={`question-${currentQuestion.id}-no-button`}>{ui.check.no}</button>
      </div>
    );
  };

  return (
    <JourneyShell>
      <section className="journey-panel journey-question" data-testid={`assessment-${tier}-panel`}>
        <div className="journey-progress" data-testid="assessment-progress">
          <span>{progress}</span>
          <div><i style={{ width: `${((currentIndex + 1) / visible.length) * 100}%` }} /></div>
        </div>
        <h1 data-testid="assessment-title">{sectionUi.title}</h1>
        {tier === "paid" && <p data-testid="assessment-intro">{sectionUi.intro}</p>}
        <h2 data-testid={`question-${currentQuestion.id}-text`}>{currentQuestion.text}</h2>
        {renderAnswer()}
        <div className="journey-question__footer">
          <Button type="button" variant="ghost" onClick={() => setCurrentIndex((index) => Math.max(0, index - 1))} disabled={currentIndex === 0 || saving} data-testid="assessment-back-button">{ui.check.back}</Button>
          {currentQuestion.allow_skip && <Button type="button" variant="ghost" onClick={() => save({ answer: "skipped" })} disabled={saving} data-testid="assessment-skip-button">{ui.check.skip}</Button>}
          {saving && <span data-testid="assessment-saving-status">{ui.check.saving}</span>}
        </div>
      </section>
    </JourneyShell>
  );
}